<template>
  <div class="dash">
    <!-- Selector de empresa -->
    <EmpresaSelector
      v-if="showEmpresaSelector"
      @empresa-selected="onEmpresaSelected"
    />

    <!-- Header -->
    <div class="dash-header">
      <div>
        <h1 class="dash-title">{{ tituloPrincipal }}</h1>
        <p class="dash-subtitle">Bienvenido, {{ userName }}.</p>
      </div>
      <div v-if="menuGroups.length > 0 || standaloneItems.length > 0" class="dash-collapse-tools">
        <button class="dash-tool-btn" type="button" :title="todasColapsadas ? 'Expandir todo' : 'Colapsar todo'" @click="todasColapsadas ? expandAllSections() : collapseAllSections()">
          <i :class="todasColapsadas ? 'bi bi-arrows-expand' : 'bi bi-arrows-collapse'"></i>
        </button>
      </div>
      <div class="dash-search-box">
        <i class="bi bi-search dash-search-icon"></i>
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="dash-search-input"
          placeholder="Buscar opciones..."
          @keydown.enter="goToFirstResult"
          @keydown.down.prevent="highlightNext"
          @keydown.up.prevent="highlightPrev"
          @keydown.escape="searchQuery = ''"
        />
        <kbd v-if="!searchQuery" class="dash-search-kbd">/</kbd>
        <button v-else class="dash-search-clear" @click="searchQuery = ''; $refs.searchInput.focus()">
          <i class="bi bi-x"></i>
        </button>
        <!-- Resultados -->
        <div v-if="searchResults.length > 0" class="dash-search-results">
          <router-link
            v-for="(item, idx) in searchResults"
            :key="item.MenuId"
            :to="item.Enlace.startsWith('/') ? item.Enlace : '/' + item.Enlace"
            :class="['dash-search-item', { 'is-active': idx === highlightIndex }]"
            @click="searchQuery = ''"
          >
            <i :class="'bi bi-' + (item.Imagen || 'grid')"></i>
            <span>{{ item.Titulo }}</span>
            <span class="dash-search-item-desc">{{ item.Descripcion || '' }}</span>
          </router-link>
        </div>
        <div v-else-if="searchQuery.length >= 2" class="dash-search-results">
          <div class="dash-search-empty">Sin resultados</div>
        </div>
      </div>
      <div v-if="empresaSeleccionada" class="dash-empresa">
        <i class="bi bi-buildings"></i>
        <span>{{ empresaSeleccionada.Nombre }}</span>
      </div>
    </div>

    <!-- Menu groups -->
    <template v-if="menuGroups.length > 0 || standaloneItems.length > 0">
      <div v-for="group in menuGroups" :key="group.parentId" class="dash-section">
        <button class="dash-section-title" type="button" @click="toggleSection(sectionKey(group))">
          <span class="dash-section-title-left">
            <i v-if="group.parentIcon" :class="'bi bi-' + group.parentIcon"></i>
            {{ group.parentTitle }}
            <span class="dash-section-count">{{ group.children.length }}</span>
          </span>
          <i :class="isSectionCollapsed(sectionKey(group)) ? 'bi bi-chevron-down' : 'bi bi-chevron-up'"></i>
        </button>
        <div v-show="!isSectionCollapsed(sectionKey(group))" class="dash-grid">
          <router-link
            v-for="item in group.children"
            :key="item.MenuId"
            :to="item.Enlace.startsWith('/') ? item.Enlace : '/' + item.Enlace"
            class="dash-card"
          >
            <div class="dash-card-icon">
              <i :class="'bi bi-' + (item.Imagen || 'grid')"></i>
            </div>
            <div class="dash-card-body">
              <span class="dash-card-title">{{ item.Titulo }}</span>
              <span class="dash-card-desc">{{ item.Descripcion || '' }}</span>
            </div>
            <i class="bi bi-chevron-right dash-card-arrow"></i>
          </router-link>
        </div>
      </div>

      <div v-if="standaloneItems.length > 0" class="dash-section">
        <button class="dash-section-title" type="button" @click="toggleSection('standalone')">
          <span class="dash-section-title-left">
            <i class="bi bi-grid"></i>
            Otras opciones
            <span class="dash-section-count">{{ standaloneItems.length }}</span>
          </span>
          <i :class="isSectionCollapsed('standalone') ? 'bi bi-chevron-down' : 'bi bi-chevron-up'"></i>
        </button>
        <div v-show="!isSectionCollapsed('standalone')" class="dash-grid">
          <router-link
            v-for="item in standaloneItems"
            :key="item.MenuId"
            :to="item.Enlace.startsWith('/') ? item.Enlace : '/' + item.Enlace"
            class="dash-card"
          >
            <div class="dash-card-icon">
              <i :class="'bi bi-' + (item.Imagen || 'grid')"></i>
            </div>
            <div class="dash-card-body">
              <span class="dash-card-title">{{ item.Titulo }}</span>
              <span class="dash-card-desc">{{ item.Descripcion || '' }}</span>
            </div>
            <i class="bi bi-chevron-right dash-card-arrow"></i>
          </router-link>
        </div>
      </div>
    </template>

    <!-- Fallback -->
    <div v-if="menuGroups.length === 0 && standaloneItems.length === 0 && !showEmpresaSelector" class="dash-empty">
      <i class="bi bi-inbox"></i>
      <p>No hay opciones de menu disponibles.</p>
    </div>
  </div>
</template>

<script>
import authService from '@/services/auth';
import EmpresaSelector from '@/shared/components/EmpresaSelector.vue';
import docEmpresaConfigService from '@/modules/compras/documentos/services/docEmpresaConfig.service';
import appTituloService from '@/shared/services/appTitulo.service';

function menuOrder(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 999999;
}

export default {
  name: 'DashboardView',
  components: { EmpresaSelector },
  data() {
    return {
      menuItems: [],
      menuGroups: [],
      standaloneItems: [],
      showEmpresaSelector: false,
      empresaSeleccionada: null,
      searchQuery: '',
      highlightIndex: 0,
      collapsedSections: {}
    };
  },
  computed: {
    userName() {
      const user = authService.getUserData();
      return user?._UsuarioMo?.Nombre || 'Usuario';
    },
    tituloPrincipal() {
      return appTituloService.state.titulo;
    },
    todasColapsadas() {
      const keys = [
        ...this.menuGroups.map(g => this.sectionKey(g)),
        ...(this.standaloneItems.length > 0 ? ['standalone'] : [])
      ];
      return keys.length === 0 || keys.every(k => this.collapsedSections[k] !== false);
    },
    searchResults() {
      if (!this.searchQuery || this.searchQuery.length < 2) return [];
      const q = this.searchQuery.toLowerCase();
      return this.menuItems
        .filter(item =>
          item.Enlace && item.Enlace !== '#' &&
          (item.Titulo?.toLowerCase().includes(q) || item.Descripcion?.toLowerCase().includes(q))
        )
        .sort((a, b) => {
          const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
          return diff !== 0 ? diff : (a.MenuId || 0) - (b.MenuId || 0);
        })
        .slice(0, 8);
    }
  },
  watch: {
    searchQuery() {
      this.highlightIndex = 0;
    }
  },
  created() {
    this.checkEmpresaSelection();
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.searchInput) this.$refs.searchInput.focus();
    });
    document.addEventListener('keydown', this._globalSlash);
    appTituloService.refrescar();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this._globalSlash);
  },
  methods: {
    async checkEmpresaSelection() {
      if (authService.hasActiveEmpresas()) {
        const emp = authService.getSelectedEmpresa();
        if (!emp) {
          // Verificar si solo hay una empresa activa → seleccionarla automáticamente
          const userData = authService.getUserData();
          const activas = (userData?._UsuarioMo?._EmpresaList || []).filter(e => e.EsActivo === true || e.EsActivo == null);
          if (activas.length === 1) {
            await authService.setSelectedEmpresa(activas[0]);
            this.empresaSeleccionada = activas[0];
            await this.loadMenuItems();
          } else {
            this.showEmpresaSelector = true;
          }
        } else {
          this.empresaSeleccionada = emp;
          await this.loadMenuItems();
        }
      } else {
        await this.loadMenuItems();
      }
    },
    onEmpresaSelected() {
      window.location.reload();
    },
    _globalSlash(e) {
      if (e.key === '/' && document.activeElement !== this.$refs.searchInput) {
        e.preventDefault();
        this.$refs.searchInput?.focus();
      }
    },
    goToFirstResult() {
      const results = this.searchResults;
      if (results.length > 0) {
        const item = results[this.highlightIndex] || results[0];
        const path = item.Enlace.startsWith('/') ? item.Enlace : '/' + item.Enlace;
        this.searchQuery = '';
        this.$router.push(path);
      }
    },
    highlightNext() {
      if (this.highlightIndex < this.searchResults.length - 1) this.highlightIndex++;
    },
    highlightPrev() {
      if (this.highlightIndex > 0) this.highlightIndex--;
    },
    async loadMenuItems() {
      const userMenu = authService.getUserMenu();
      if (!userMenu || !Array.isArray(userMenu)) {
        this.menuItems = [];
        this.menuGroups = [];
        this.standaloneItems = [];
        return;
      }

      const activeItems = userMenu.filter(item => item.EsActivo === true);

      let items = activeItems
        .filter(item => item.Grupo === 1)
        .sort((a, b) => {
          const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
          return diff !== 0 ? diff : (a.MenuId || 0) - (b.MenuId || 0);
        });

      items = await this.filterDocMenusByEmpresaConfig(items);
      this.menuItems = items;

      const parentIds = new Set();
      const parentItems = {};
      const byParent = {};

      this.menuItems.forEach(item => {
        if (item.MenuIdPadre > 0) parentIds.add(item.MenuIdPadre);
      });

      parentIds.forEach(id => { byParent[id] = []; });

      this.menuGroups = [];
      this.standaloneItems = [];

      this.menuItems.forEach(item => {
        if (item.MenuIdPadre > 0 && parentIds.has(item.MenuIdPadre)) {
          byParent[item.MenuIdPadre].push(item);
          if (!parentItems[item.MenuIdPadre]) {
            const p = activeItems.find(m => m.MenuId === item.MenuIdPadre);
            if (p) parentItems[item.MenuIdPadre] = p;
          }
        } else if (parentIds.has(item.MenuId)) {
          parentItems[item.MenuId] = item;
        } else if (!item.MenuIdPadre || item.MenuIdPadre === 0) {
          this.standaloneItems.push(item);
        }
      });

      Object.keys(byParent).forEach(pid => {
        const children = byParent[pid];
        const parent = parentItems[pid];
        if (children.length > 0 && parent) {
          children.sort((a, b) => {
            const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
            return diff !== 0 ? diff : (a.MenuId || 0) - (b.MenuId || 0);
          });
          this.menuGroups.push({
            parentId: pid,
            parentTitle: parent.Titulo,
            parentIcon: parent.Imagen,
            order: parent.Orden,
            children
          });
        }
      });

      this.menuGroups.sort((a, b) => {
        const diff = menuOrder(a.order) - menuOrder(b.order);
        return diff !== 0 ? diff : Number(a.parentId) - Number(b.parentId);
      });
      this.standaloneItems.sort((a, b) => {
        const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
        return diff !== 0 ? diff : (a.MenuId || 0) - (b.MenuId || 0);
      });

      const totalOpciones = this.menuGroups.reduce((sum, g) => sum + g.children.length, 0) + this.standaloneItems.length;
      if (totalOpciones > 15) {
        this.collapseAllSections();
      } else {
        this.expandAllSections();
      }
    },
    sectionKey(group) {
      return `group-${group.parentId}`;
    },
    isSectionCollapsed(key) {
      return this.collapsedSections[key] !== false;
    },
    toggleSection(key) {
      this.collapsedSections = {
        ...this.collapsedSections,
        [key]: !this.isSectionCollapsed(key)
      };
    },
    expandAllSections() {
      const next = {};
      this.menuGroups.forEach(group => { next[this.sectionKey(group)] = false; });
      if (this.standaloneItems.length > 0) next.standalone = false;
      this.collapsedSections = next;
    },
    collapseAllSections() {
      const next = {};
      this.menuGroups.forEach(group => { next[this.sectionKey(group)] = true; });
      if (this.standaloneItems.length > 0) next.standalone = true;
      this.collapsedSections = next;
    },
    async filterDocMenusByEmpresaConfig(items) {
      if (!this.empresaSeleccionada) return items;
      try {
        const config = await docEmpresaConfigService.leer();
        const activeDocs = new Set(
          (config.Items || [])
            .filter(x => x.Activo === true || x.Activo === undefined)
            .map(x => String(x.TipoDocumento || '').toUpperCase())
        );
        if (activeDocs.size === 0) return items;

        return items.filter(item => {
          const tipo = this.docTipoFromMenu(item);
          return !tipo || activeDocs.has(tipo);
        });
      } catch (err) {
        console.warn('No se pudo filtrar menu por configuracion de documentos', err);
        return items;
      }
    },
    docTipoFromMenu(item) {
      const path = String(item?.Enlace || '').trim().toLowerCase();
      const map = {
        '/compras/req': 'REQ',
        '/compras/soc': 'SOC',
        '/compras/scot': 'SCOT',
        '/compras/cotcom': 'COTP',
        '/compras/oc': 'OC',
        '/compras/em': 'ENTM',
        '/compras/fac': 'FACP',
        '/compras/deve': 'DEVP',
        '/ventas/cot': 'COTV',
        '/ventas/ove': 'OV',
        '/ventas/entiven': 'REM',
        '/ventas/fcve': 'FACV',
        '/ventas/deve': 'DEVV'
      };
      return map[path] || null;
    }
  }
};
</script>

<style scoped>
.dash {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem;
  font-family: 'Outfit', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ========== Header ========== */
.dash-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.dash-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.dash-subtitle {
  font-size: 0.9rem;
  color: #718096;
  margin: 0.2rem 0 0;
}

.dash-empresa {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e40af;
}

.dash-empresa i {
  font-size: 1rem;
}

.dash-collapse-tools {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  align-self: center;
}

.dash-tool-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  transition: all 0.16s ease;
}

.dash-tool-btn:hover {
  border-color: #1272c4;
  color: #1272c4;
  background: #eff6ff;
}

/* ========== Search ========== */
.dash-search-box {
  position: relative;
  flex: 1;
  max-width: 420px;
  min-width: 200px;
}

.dash-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.85rem;
  pointer-events: none;
}

.dash-search-input {
  width: 100%;
  padding: 0.55rem 2.2rem 0.55rem 2.2rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: inherit;
  color: #2d3748;
  background: #f8fafc;
  outline: none;
  transition: all 0.2s;
}

.dash-search-input:focus {
  border-color: #1272c4;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(18, 114, 196, 0.1);
}

.dash-search-input::placeholder {
  color: #a0aec0;
}

.dash-search-kbd {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: #edf2f7;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 0px 6px;
  font-size: 0.7rem;
  color: #a0aec0;
  font-family: monospace;
  line-height: 1.6;
}

.dash-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  font-size: 1rem;
  padding: 0 4px;
  line-height: 1;
}

.dash-search-clear:hover {
  color: #4a5568;
}

.dash-search-results {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  z-index: 50;
  max-height: 320px;
  overflow-y: auto;
}

.dash-search-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.85rem;
  text-decoration: none;
  color: #2d3748;
  font-size: 0.85rem;
  transition: background 0.15s;
  cursor: pointer;
}

.dash-search-item:hover,
.dash-search-item.is-active {
  background: #eff6ff;
}

.dash-search-item i {
  color: #1272c4;
  font-size: 0.9rem;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.dash-search-item-desc {
  margin-left: auto;
  font-size: 0.72rem;
  color: #a0aec0;
}

.dash-search-empty {
  padding: 1rem;
  text-align: center;
  color: #a0aec0;
  font-size: 0.8rem;
}

/* ========== Section ========== */
.dash-section {
  margin-bottom: 2rem;
}

.dash-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #edf2f7;
  border-left: 0;
  border-right: 0;
  border-top: 0;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.dash-section-title i {
  font-size: 0.9rem;
  color: #a0aec0;
}

.dash-section-title:hover {
  color: #2d3748;
}

.dash-section-title:hover i {
  color: #1272c4;
}

.dash-section-title-left {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 0.5rem;
}

.dash-section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #edf2f7;
  color: #718096;
  font-size: 0.7rem;
  font-weight: 700;
}

/* ========== Grid ========== */
.dash-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0.75rem;
}

/* ========== Card ========== */
.dash-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.15rem;
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
}

.dash-card:hover {
  border-color: #1272c4;
  box-shadow: 0 2px 8px rgba(18, 114, 196, 0.08);
  transform: translateY(-1px);
}

.dash-card-icon {
  width: 42px;
  height: 42px;
  border-radius: 9px;
  background: #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #1272c4;
  flex-shrink: 0;
  transition: all 0.2s;
}

.dash-card:hover .dash-card-icon {
  background: linear-gradient(135deg, #0d5ca0, #1272c4);
  color: #ffffff;
}

.dash-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dash-card-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.2;
}

.dash-card-desc {
  font-size: 0.75rem;
  color: #a0aec0;
  margin-top: 0.1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-card-arrow {
  color: #cbd5e0;
  font-size: 0.75rem;
  flex-shrink: 0;
  transition: all 0.2s;
}

.dash-card:hover .dash-card-arrow {
  color: #1272c4;
  transform: translateX(2px);
}

/* ========== Empty ========== */
.dash-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #a0aec0;
}

.dash-empty i {
  font-size: 2.5rem;
  color: #e2e8f0;
}

.dash-empty p {
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
}

/* ========== Responsive ========== */
@media (max-width: 640px) {
  .dash-grid {
    grid-template-columns: 1fr;
  }

  .dash-header {
    flex-direction: column;
  }

  .dash-collapse-tools {
    align-self: flex-start;
  }
}
</style>
